import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { Loader2 } from 'lucide-react';

// Must match OnboardingWizard's ONBOARDING_KEY — was 'onboarding_v1_done'
// and broke the localStorage fallback when the wizard was bumped to v2,
// so /cctv-design kept bouncing back to /dashboard during the race between
// wizard completion and the user_preferences write round-trip.
const ONBOARDING_KEY = 'onboarding_v2_done';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const { preferences, loading: prefsLoading } = useUserPreferences();
  const location = useLocation();

  if (authLoading || prefsLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  // Onboarding is mandatory — redirect to dashboard if not completed
  // Skip this check when already on /dashboard (to avoid infinite redirect)
  const isDashboard = location.pathname === '/dashboard' || location.pathname.endsWith('/dashboard');
  const onboardingDone = preferences.onboardingCompleted || !!localStorage.getItem(ONBOARDING_KEY);
  if (!isDashboard && !onboardingDone) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
